import { useState } from 'react'
import { Row, Col, Card, Form, Button, Badge, Table, Container, Spinner, Pagination } from 'react-bootstrap'
import { useQuery } from '@tanstack/react-query'
import { api } from '../../services/api'

export const TicketsPage = () => {
  const [filters, setFilters] = useState({
    status: '',
    priority: '',
    assigneeId: '',
    q: ''
  })
  const [page, setPage] = useState(1)
  const itemsPerPage = 10

  const { data: tickets, isLoading } = useQuery({
    queryKey: ['tickets', filters, page],
    queryFn: async () => {
      const params = new URLSearchParams()
      Object.entries(filters).forEach(([key, value]) => {
        if (value) params.append(key, value)
      })
      params.append('page', page.toString())
      params.append('pageSize', itemsPerPage.toString())
      
      const response = await api.get(`/tickets?${params.toString()}`)
      return response.data
    }
  })

  const totalPages = tickets?.meta?.totalPages || 0

  const handlePageChange = (newPage: number) => {
    setPage(newPage)
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const handleFilterChange = (newFilters: typeof filters) => {
    setFilters(newFilters)
    setPage(1) // Reset to page 1 when filters change
  }

  const getPriorityBadge = (priority: string) => {
    const variants = {
      'Low': 'secondary',
      'Medium': 'primary',
      'High': 'warning',
      'Urgent': 'danger'
    }
    return <Badge bg={variants[priority as keyof typeof variants] || 'secondary'}>{priority}</Badge>
  }

  const getStatusBadge = (status: string) => {
    const variants = {
      'OPEN': 'primary',
      'IN_PROGRESS': 'warning',
      'PENDING_CUSTOMER': 'info',
      'RESOLVED': 'success',
      'CLOSED': 'secondary',
      'REOPENED': 'danger'
    }
    return <Badge bg={variants[status as keyof typeof variants] || 'secondary'}>{status}</Badge>
  }

  if (isLoading) {
    return (
      <Container fluid className="py-4">
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Loading...</span>
          </Spinner>
        </div>
      </Container>
    )
  }

  return (
    <Container fluid className="py-3">
      {/* Header */}
      <div className="d-flex flex-column flex-sm-row justify-content-between align-items-start align-items-sm-center mb-3">
        <div className="mb-2 mb-sm-0">
          <h2 className="mb-1">All Tickets</h2>
          <p className="text-muted mb-0 small">View and manage all support tickets</p>
        </div>
        <Button variant="primary" href="/tickets/new" className="d-flex align-items-center">
          <i className="fas fa-plus me-2"></i>
          New Ticket
        </Button>
      </div>

      {/* Filters */}
      <Card className="mb-3 border-0 shadow-sm">
        <Card.Header className="bg-white border-bottom py-2">
          <h6 className="mb-0">
            <i className="fas fa-filter me-2 text-primary"></i>
            Filters
          </h6>
        </Card.Header>
        <Card.Body>
          <Row>
            <Col xs={12} sm={6} md={3}>
              <Form.Group>
                <Form.Label>Status</Form.Label>
                <Form.Select
                  value={filters.status}
                  onChange={(e) => handleFilterChange({ ...filters, status: e.target.value })}
                >
                  <option value="">All Statuses</option>
                  <option value="OPEN">Open</option>
                  <option value="IN_PROGRESS">In Progress</option>
                  <option value="PENDING_CUSTOMER">Pending Customer</option>
                  <option value="RESOLVED">Resolved</option>
                  <option value="CLOSED">Closed</option>
                  <option value="REOPENED">Reopened</option>
                </Form.Select>
              </Form.Group>
            </Col>
            
            <Col xs={12} sm={6} md={3}>
              <Form.Group>
                <Form.Label>Priority</Form.Label>
                <Form.Select
                  value={filters.priority}
                  onChange={(e) => handleFilterChange({ ...filters, priority: e.target.value })}
                >
                  <option value="">All Priorities</option>
                  <option value="Low">Low</option>
                  <option value="Medium">Medium</option>
                  <option value="High">High</option>
                  <option value="Urgent">Urgent</option>
                </Form.Select>
              </Form.Group>
            </Col>
            
            <Col xs={12} sm={6} md={4}>
              <Form.Group>
                <Form.Label>Search</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Search tickets..."
                  value={filters.q}
                  onChange={(e) => handleFilterChange({ ...filters, q: e.target.value })}
                />
              </Form.Group>
            </Col>
            
            <Col xs={12} sm={6} md={2}>
              <Form.Group>
                <Form.Label>&nbsp;</Form.Label>
                <div>
                  <Button
                    variant="outline-secondary"
                    onClick={() => handleFilterChange({ status: '', priority: '', assigneeId: '', q: '' })}
                  >
                    Clear
                  </Button>
                </div>
              </Form.Group>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Tickets Table */}
      <Card className="border-0 shadow-sm">
        <Card.Header className="bg-white border-bottom">
          <div className="d-flex justify-content-between align-items-center">
            <h5 className="mb-0">
              <i className="fas fa-ticket-alt me-2 text-primary"></i>
              Tickets
            </h5>
            {tickets?.meta && (
              <small className="text-muted">
                Showing {((page - 1) * itemsPerPage) + 1}-{Math.min(page * itemsPerPage, tickets.meta.total)} of {tickets.meta.total}
              </small>
            )}
          </div>
        </Card.Header>
        <Card.Body className="p-0">
          <Table responsive hover className="mb-0">
            <thead className="bg-light">
              <tr>
                <th>ID</th>
                <th>Subject</th>
                <th>Status</th>
                <th>Priority</th>
                <th>Requester</th>
                <th>Assignee</th>
                <th>Activity</th>
                <th>Created</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {tickets?.data?.map((ticket: any) => (
                <tr key={ticket.id}>
                  <td>#{ticket.id.slice(-8)}</td>
                  <td style={{ maxWidth: '350px', wordBreak: 'break-word', overflowWrap: 'break-word' }}>
                    <a href={`/tickets/${ticket.id}`} className="text-decoration-none">
                      {ticket.subject}
                    </a>
                  </td>
                  <td>{getStatusBadge(ticket.status)}</td>
                  <td>{getPriorityBadge(ticket.priority)}</td>
                  <td>{ticket.requester.name}</td>
                  <td>{ticket.assignee?.name || 'Unassigned'}</td>
                  <td>
                    <div className="d-flex gap-2">
                      <Badge 
                        bg="light" 
                        text="dark" 
                        className="d-flex align-items-center gap-1"
                        title={`${ticket._count?.comments || 0} comments`}
                      >
                        <i className="fas fa-comments"></i>
                        {ticket._count?.comments || 0}
                      </Badge>
                      <Badge 
                        bg={ticket._count?.attachments > 0 ? 'info' : 'light'} 
                        text={ticket._count?.attachments > 0 ? 'white' : 'dark'}
                        className="d-flex align-items-center gap-1"
                        title={`${ticket._count?.attachments || 0} attachment${ticket._count?.attachments !== 1 ? 's' : ''}`}
                      >
                        <i className="fas fa-paperclip"></i>
                        {ticket._count?.attachments || 0}
                      </Badge>
                    </div>
                  </td>
                  <td>{new Date(ticket.createdAt).toLocaleDateString()}</td>
                  <td>
                    <Button variant="outline-primary" size="sm" href={`/tickets/${ticket.id}`}>
                      View
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Card.Body>
      </Card>

      {tickets?.data?.length === 0 && (
        <div className="text-center py-5">
          <i className="fas fa-inbox text-muted" style={{ fontSize: '3rem' }}></i>
          <h5 className="mt-3 text-muted">No tickets found</h5>
          <p className="text-muted">Try adjusting your filters or create a new ticket</p>
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="d-flex justify-content-center mt-3">
          <Pagination>
            <Pagination.First onClick={() => handlePageChange(1)} disabled={page === 1} />
            <Pagination.Prev onClick={() => handlePageChange(page - 1)} disabled={page === 1} />
            
            {[...Array(totalPages)].map((_, i) => {
              const pageNum = i + 1
              if (
                pageNum === 1 ||
                pageNum === totalPages ||
                (pageNum >= page - 1 && pageNum <= page + 1)
              ) {
                return (
                  <Pagination.Item
                    key={pageNum}
                    active={pageNum === page}
                    onClick={() => handlePageChange(pageNum)}
                  >
                    {pageNum}
                  </Pagination.Item>
                )
              } else if (pageNum === page - 2 || pageNum === page + 2) {
                return <Pagination.Ellipsis key={pageNum} disabled />
              }
              return null
            })}
            
            <Pagination.Next onClick={() => handlePageChange(page + 1)} disabled={page === totalPages} />
            <Pagination.Last onClick={() => handlePageChange(totalPages)} disabled={page === totalPages} />
          </Pagination>
        </div>
      )}
    </Container>
  )
}
